import { useEffect, useState } from 'react'
import {
  Badge, Button, Card, CloseButton, Dialog, HStack, Portal, Progress, Stack,
  Text,
} from '@chakra-ui/react'
import { LuExternalLink, LuTrash2 } from 'react-icons/lu'
import { useAuth } from '../../contexts/AuthContext'
import { toaster } from '../ui/toaster'
import { useTranslation } from '../../i18n'
import {
  deleteVideoExport, exportPageUrl, subscribeEventExports,
  subscribeExportProgress,
} from '../../services/videoExport'

const TONE = {
  queued: 'gray', processing: 'blue', zipping: 'blue',
  ready: 'green', error: 'red',
}

/** One row: live progress while it runs, a link to its page once it is ready. */
function ExportRow({ job }) {
  const { t } = useTranslation()
  const { user, isSysAdmin } = useAuth()
  const [progress, setProgress] = useState({ done: 0, failed: 0 })
  const [confirming, setConfirming] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const running = job.status === 'processing' || job.status === 'zipping'

  // Mirrors exportDeleteDecision on the server, which is what actually
  // decides. This only governs whether the button is worth showing.
  const canDelete = isSysAdmin || (!!user && job.createdBy?.uid === user.uid)

  const remove = async () => {
    setDeleting(true)
    try {
      await deleteVideoExport(job.id)
      toaster.create({ title: t('exportList.deleted'), type: 'success' })
      setConfirming(false)
    } catch (error) {
      console.error('Could not delete export:', error)
      toaster.create({ title: t('exportList.deleteFailed'), type: 'error' })
    } finally {
      setDeleting(false)
    }
  }

  useEffect(() => {
    if (!running) return undefined
    return subscribeExportProgress(job.id, setProgress)
  }, [job.id, running])

  return (
    <Card.Root size="sm">
      <Card.Body>
        <Stack gap={2}>
          <HStack justify="space-between">
            <HStack gap={3}>
              <Badge colorPalette={TONE[job.status] || 'gray'}>
                {/* t() returns the key itself for an unknown status, which
                    would render "exportList.status.foo" on screen. TONE is the
                    set we have copy for. */}
                {TONE[job.status]
                  ? t(`exportList.status.${job.status}`)
                  : job.status}
              </Badge>
              <Text fontSize="sm" color="fg.muted">
                {t('exportList.videos', { count: job.total })}
                {' · '}
                {t('exportList.recipients', { count: (job.recipients || []).length })}
              </Text>
            </HStack>
            <HStack gap={2}>
              {job.status === 'ready' && (
                <Button asChild size="xs" variant="outline">
                  <a href={exportPageUrl(job.id)} target="_blank" rel="noreferrer">
                    <LuExternalLink /> {t('exportList.open')}
                  </a>
                </Button>
              )}
              {canDelete && (
                <Button
                  size="xs"
                  variant="ghost"
                  colorPalette="red"
                  aria-label={t('exportList.delete')}
                  onClick={() => setConfirming(true)}
                >
                  <LuTrash2 />
                </Button>
              )}
            </HStack>
          </HStack>

          {running && (
            <Progress.Root value={progress.done} max={job.total || 1} size="sm">
              <Progress.Track><Progress.Range /></Progress.Track>
            </Progress.Root>
          )}
          {running && (
            <Text fontSize="xs" color="fg.muted">
              {t('exportList.progress', { done: progress.done, total: job.total })}
              {progress.failed > 0 && ` · ${t('exportList.failed', { count: progress.failed })}`}
            </Text>
          )}
          <Dialog.Root
            open={confirming}
            onOpenChange={(e) => setConfirming(e.open)}
            role="alertdialog"
          >
            <Portal>
              <Dialog.Backdrop />
              <Dialog.Positioner>
                <Dialog.Content>
                  <Dialog.Header>
                    <Dialog.Title>{t('exportList.deleteTitle')}</Dialog.Title>
                    <Dialog.CloseTrigger asChild>
                      <CloseButton size="sm" />
                    </Dialog.CloseTrigger>
                  </Dialog.Header>
                  <Dialog.Body>
                    <Text>
                      {t('exportList.deleteConfirm', {
                        count: (job.recipients || []).length,
                      })}
                    </Text>
                  </Dialog.Body>
                  <Dialog.Footer>
                    <Dialog.ActionTrigger asChild>
                      <Button variant="outline">{t('common.cancel')}</Button>
                    </Dialog.ActionTrigger>
                    <Button colorPalette="red" loading={deleting} onClick={remove}>
                      {t('exportList.deleteButton')}
                    </Button>
                  </Dialog.Footer>
                </Dialog.Content>
              </Dialog.Positioner>
            </Portal>
          </Dialog.Root>

          {job.status === 'error' && job.error?.message && (
            <Text fontSize="xs" color="red.300">{job.error.message}</Text>
          )}
          {job.status === 'ready' && job.mailError && (
            <Text fontSize="xs" color="red.300">{t('exportList.mailFailed')}</Text>
          )}
        </Stack>
      </Card.Body>
    </Card.Root>
  )
}

/**
 * The event's exports, live.
 *
 * Every staff member sees a running export, not only whoever started it —
 * otherwise a second admin's obvious move is to start a second one.
 */
export function ExportList({ eventId }) {
  const { t } = useTranslation()
  const [jobs, setJobs] = useState([])

  useEffect(() => subscribeEventExports(eventId, setJobs), [eventId])

  if (!jobs.length) return null

  return (
    <Stack gap={3}>
      <Text fontWeight="medium">{t('exportList.title')}</Text>
      {jobs.slice(0, 5).map((job) => <ExportRow key={job.id} job={job} />)}
    </Stack>
  )
}
